type ExecuteRecaptcha = (action: string) => Promise<string>;

type CacheEntry = {
  token: string;
  timestampMs: number;
};

const TOKEN_TTL_MS = 0;
const tokenCache = new Map<string, CacheEntry>();
let globalExecutor: ExecuteRecaptcha | undefined;

export const setRecaptchaExecutor = (executor?: ExecuteRecaptcha): void => {
  globalExecutor = executor;
};

export const isRecaptchaReady = (): boolean => Boolean(globalExecutor);

export const getCachedRecaptchaToken = async (
  action: string,
  executeRecaptcha?: ExecuteRecaptcha
): Promise<string> => {
  const executor = executeRecaptcha || globalExecutor;
  if (!executor) {
    return "";
  }
  const cached = tokenCache.get(action);
  if (cached && Date.now() - cached.timestampMs < TOKEN_TTL_MS) {
    return cached.token;
  }
  try {
    const token = await executor(action);
    if (token) {
      tokenCache.set(action, { token, timestampMs: Date.now() });
    }
    return token;
  } catch {
    return "";
  }
};

export const prefetchRecaptchaToken = (
  action: string,
  executeRecaptcha?: ExecuteRecaptcha
): void => {
  const executor = executeRecaptcha || globalExecutor;
  if (!executor) {
    return;
  }
  const cached = tokenCache.get(action);
  if (cached && Date.now() - cached.timestampMs < TOKEN_TTL_MS) {
    return;
  }
  try {
    executor(action)
      .then((token) => {
        if (token) {
          tokenCache.set(action, { token, timestampMs: Date.now() });
        }
      })
      .catch(() => {});
  } catch {
    // Ignore executor errors during warmup; caller should handle retries.
  }
};

export const warmupRecaptcha = (action: string): void => {
  prefetchRecaptchaToken(action);
};
